/**
 * Der Wrapping-Key entsperrt den lokalen Schlüsselspeicher (SECURITY.md §3.5).
 *
 * Drei Wege hinein, in dieser Reihenfolge:
 *
 *   1. **WebAuthn-PRF** — der Passkey liefert ein deterministisches Geheimnis.
 *      Der Oma-Test-taugliche Weg: Fingerabdruck auflegen, fertig, kein Passwort.
 *   2. **Passphrase** — für Geräte ohne PRF-Unterstützung.
 *   3. **Recovery-Blatt** — 12 Wörter, wenn alle Geräte weg sind (ADR-008).
 *
 * Der Wrapping-Key selbst wird nie gespeichert. Er entsteht bei jedem Entsperren
 * neu und liegt nur im Speicher, als nicht-exportierbarer `CryptoKey`.
 */
const HKDF_INFO = 'family-hub/keystore-wrapping-key/v1';
/**
 * PBKDF2 statt Argon2id, mit Begründung:
 *
 * Argon2 gibt es im Browser nur als WASM-Zusatzabhängigkeit. PBKDF2-HMAC-SHA256
 * ist in WebCrypto nativ vorhanden — kein Nachladen, läuft auch auf alten
 * Geräten. Der Preis ist die schwächere Speicherhärte gegenüber GPU-Angriffen;
 * die 600.000 Runden entsprechen der OWASP-Empfehlung für genau diesen Fall.
 *
 * Serverseitig, wo Argon2id verfügbar ist, wird es auch genutzt
 * (`modules/auth/passphrase.ts`). Diese Abweichung gilt nur für den Browser.
 */
const PBKDF2_ITERATIONS = 600_000;
/** Aus WebAuthn-PRF: das Geheimnis kommt vom Authenticator, nicht aus dem Kopf. */
export async function fromPrf(prfOutput) {
    const material = await crypto.subtle.importKey('raw', prfOutput, 'HKDF', false, ['deriveKey']);
    return crypto.subtle.deriveKey({
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new Uint8Array(0),
        info: new TextEncoder().encode(HKDF_INFO),
    }, material, { name: 'AES-GCM', length: 256 }, false, // nicht exportierbar
    ['encrypt', 'decrypt']);
}
/** Aus einer Passphrase. `salt` ist gerätefest und liegt neben dem Speicher. */
export async function fromPassphrase(passphrase, salt) {
    if (passphrase.length < 12) {
        throw new Error('Die Passphrase muss mindestens 12 Zeichen haben');
    }
    const material = await crypto.subtle.importKey('raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveKey']);
    return crypto.subtle.deriveKey({ name: 'PBKDF2', hash: 'SHA-256', salt: salt, iterations: PBKDF2_ITERATIONS }, material, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
/** Aus dem Recovery-Seed (siehe `recovery.ts`). */
export async function fromRecoverySeed(seed) {
    const material = await crypto.subtle.importKey('raw', seed, 'HKDF', false, ['deriveKey']);
    return crypto.subtle.deriveKey({
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new Uint8Array(0),
        info: new TextEncoder().encode(`${HKDF_INFO}/recovery`),
    }, material, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
export function neuesSalz() {
    return crypto.getRandomValues(new Uint8Array(16));
}
// --- Speicherschlüssel -------------------------------------------------------
/**
 * Die Inhalte hängen NICHT direkt an einem der drei Wege oben, sondern an einem
 * zufälligen Speicherschlüssel. Die Wege verschlüsseln nur ihn.
 *
 * Ohne diese Zwischenstufe gäbe es pro Speicher genau einen Weg hinein: Wer mit
 * einer Passphrase eingerichtet hat, käme nie mit dem Passkey hinein, und ein
 * Wechsel der Passphrase würde alle Inhalte unlesbar machen — man müsste sie
 * neu verschlüsseln und hätte bei einem Abbruch mittendrin nichts mehr.
 *
 * Mit der Zwischenstufe ist ein zusätzlicher Weg eine einzige kleine
 * Verschlüsselung von 32 Byte.
 */
export function neuerSpeicherschluessel() {
    return crypto.getRandomValues(new Uint8Array(32));
}
/** Rohbytes zu einem nicht exportierbaren AES-GCM-Schlüssel machen. */
export async function speicherschluessel(roh) {
    if (roh.length !== 32)
        throw new Error('Speicherschlüssel muss 32 Byte haben');
    return crypto.subtle.importKey('raw', roh, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}
// --- Verschlüsseln und Entschlüsseln des Speichers ---------------------------
/** AES-256-GCM. Die Nonce steht vorn, weil sie zum Ciphertext gehört. */
export async function versiegeln(key, klartext) {
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, key, klartext));
    const out = new Uint8Array(nonce.length + ct.length);
    out.set(nonce, 0);
    out.set(ct, nonce.length);
    return out;
}
export async function entsiegeln(key, paket) {
    if (paket.length < 13)
        throw new Error('Paket zu kurz');
    // slice statt subarray: WebCrypto verlangt einen eigenständigen Puffer, keine
    // Sicht auf einen fremden. Die Kopie ist bei Schlüsselmaterial vernachlässigbar.
    const nonce = paket.slice(0, 12);
    const ct = paket.slice(12);
    return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, key, ct));
}
//# sourceMappingURL=wrapping-key.js.map